import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from './ui/table';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertTitle, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import { ConfirmDialog } from './ConfirmDialog';
import { AlertTriangle, Merge, ShieldCheck, RefreshCw, Package, Wand2, PenSquare, Search, Link2, BookOpen, ChevronDown, ChevronUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Panel de estabilización del catálogo:
 *  - Sección A: Homologación de altas por REM (pending_catalog_review=true).
 *  - Sección B: Fusión de artículos duplicados.
 *
 * Ambas conviven porque la fusión es la herramienta que resuelve la mayoría de
 * las homologaciones (el operador REM tipeó un duplicado que ya existía).
 */
export function ArticleMergePanel({ onMergeComplete }) {
  const [pending, setPending] = useState(null);
  const [candidates, setCandidates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Preview modal
  const [mergeDialog, setMergeDialog] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // {label, onConfirm}

  // Bulk auto-merge (v3.30.9)
  const [bulkPlan, setBulkPlan] = useState(null); // { total_groups, groups_ready_to_merge, groups_skipped, records_to_move_total, plan[], skipped[] }
  const [bulkApplyResult, setBulkApplyResult] = useState(null);

  // v3.41.0 — Modal "Corregir / Vincular"
  const [fixDialog, setFixDialog] = useState(null); // { pending, mode:'link'|'rename', query, results, loading, newName, newCategory, newUnit }

  // v3.45.2 — Fusión manual libre (duplicados que el algoritmo no detecta)
  const [manualPrimary, setManualPrimary] = useState(null);   // artículo elegido como principal
  const [manualAbsorbed, setManualAbsorbed] = useState(null); // artículo a absorber
  const [manualQuery, setManualQuery] = useState({ primary: '', absorbed: '' });
  const [manualResults, setManualResults] = useState({ primary: [], absorbed: [] });
  const [manualSearching, setManualSearching] = useState({ primary: false, absorbed: false });

  const searchManual = async (slot, q) => {
    setManualQuery(prev => ({ ...prev, [slot]: q }));
    if (q.length < 2) { setManualResults(prev => ({ ...prev, [slot]: [] })); return; }
    setManualSearching(prev => ({ ...prev, [slot]: true }));
    try {
      const r = await axios.get(`${BACKEND_URL}/api/article-merge/search`, {
        params: { q, limit: 12 }, withCredentials: true,
      });
      setManualResults(prev => ({ ...prev, [slot]: r.data.results || [] }));
    } catch { setManualResults(prev => ({ ...prev, [slot]: [] })); }
    finally { setManualSearching(prev => ({ ...prev, [slot]: false })); }
  };

  const selectManual = (slot, article) => {
    if (slot === 'primary') setManualPrimary(article);
    else setManualAbsorbed(article);
    setManualResults(prev => ({ ...prev, [slot]: [] }));
    setManualQuery(prev => ({ ...prev, [slot]: article.name }));
  };

  const openManualMerge = async () => {
    if (!manualPrimary || !manualAbsorbed) return;
    if (manualPrimary.article_id === manualAbsorbed.article_id) {
      toast.error('Elegiste el mismo artículo en ambos campos'); return;
    }
    await openMerge(manualPrimary.article_id, [manualAbsorbed.article_id]);
  };

  // v3.41.1 — Guía embebida (reubicada desde /help/farmaceutico)
  const [guideOpen, setGuideOpen] = useState(false);
  const [guideContent, setGuideContent] = useState(null);
  const toggleGuide = async () => {
    const next = !guideOpen;
    setGuideOpen(next);
    if (next && guideContent === null) {
      try {
        const r = await fetch('/docs/FARMACEUTICO_HOMOLOGACION_v3.40.6.md');
        setGuideContent(r.ok ? await r.text() : '*No se pudo cargar la guía.*');
      } catch {
        setGuideContent('*No se pudo cargar la guía.*');
      }
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/article-merge/pending-reviews`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/article-merge/candidates`, { withCredentials: true }),
      ]);
      setPending(p.data);
      setCandidates(c.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo cargar el panel de artículos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const homologate = async (article_id, name) => {
    setBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/article-merge/pending-reviews/${article_id}/homologate`, {}, { withCredentials: true });
      const m = r.data?.materialization;
      if (m && m.materialized > 0) {
        toast.success(`Homologado: ${name} · materializó ${m.materialized} ingreso(s) congelado(s) (${m.total_qty} un.) al stock del área`);
      } else {
        toast.success(`Homologado: ${name}`);
      }
      load();
      onMergeComplete?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo homologar');
    } finally { setBusy(false); }
  };

  const openMerge = async (primary_id, absorbed_ids) => {
    try {
      const r = await axios.post(`${BACKEND_URL}/api/article-merge/preview`,
        { primary_id, absorbed_ids }, { withCredentials: true });
      setMergeDialog({ primary_id, absorbed_ids, preview: r.data });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo calcular la fusión');
    }
  };

  const confirmMerge = async () => {
    if (!mergeDialog) return;
    setBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/article-merge`, {
        primary_id: mergeDialog.primary_id,
        absorbed_ids: mergeDialog.absorbed_ids,
      }, { withCredentials: true });
      toast.success(`Fusión aplicada: ${r.data.moved_total} registro(s) movidos · ${r.data.mirror_result?.merged || 0} espejos fusionados, ${r.data.mirror_result?.renamed || 0} renombrados`);
      setMergeDialog(null);
      // Limpiar buscador manual
      setManualPrimary(null); setManualAbsorbed(null);
      setManualQuery({ primary: '', absorbed: '' });
      load();
      onMergeComplete?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo aplicar la fusión');
    } finally { setBusy(false); }
  };

  // v3.30.9 · Fusión masiva asistida
  const runBulkDryRun = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/article-merge/bulk-auto`,
        { dry_run: true }, { withCredentials: true });
      setBulkPlan(r.data);
      if (r.data.groups_ready_to_merge === 0) {
        toast.info('No hay grupos que cumplan la regla (Manual/Compras como principal).');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo calcular el plan masivo');
    } finally { setBusy(false); }
  };

  const applyBulk = async () => {
    if (!bulkPlan?.groups_ready_to_merge) return;
    setBusy(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/article-merge/bulk-auto`,
        { dry_run: false }, { withCredentials: true });
      setBulkApplyResult(r.data);
      setBulkPlan(null);
      toast.success(`Fusión masiva aplicada: ${r.data.groups_merged} grupos · ${r.data.records_moved_total} registros movidos${r.data.errors?.length ? ` · ${r.data.errors.length} errores` : ''}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo aplicar la fusión masiva');
    } finally { setBusy(false); }
  };

  // v3.41.0 — Abrir modal de corrección para una ficha pendiente
  const openFixDialog = (pending) => {
    const initialQuery = pending.name || '';
    setFixDialog({
      pending,
      mode: 'link',
      query: initialQuery,
      results: [],
      searching: false,
      newName: pending.name || '',
      newCategory: pending.category || '',
      newUnit: pending.unit || '',
    });
    // Dispara la primera búsqueda con el nombre tipeado (ayuda a encontrar el
    // canónico incluso con typos — el score fuzzy >= 0.55 ya los agrupa).
    if (initialQuery.length >= 2) {
      setTimeout(() => searchCanonical(initialQuery), 100);
    }
  };

  const searchCanonical = async (q) => {
    if (!q || q.trim().length < 2) {
      setFixDialog((d) => d ? { ...d, results: [], searching: false } : d);
      return;
    }
    setFixDialog((d) => d ? { ...d, searching: true } : d);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/article-merge/search`, {
        params: { q, limit: 15 },
        withCredentials: true,
      });
      setFixDialog((d) => d ? { ...d, results: r.data.results || [], searching: false } : d);
    } catch (e) {
      setFixDialog((d) => d ? { ...d, results: [], searching: false } : d);
      toast.error(e.response?.data?.detail || 'No se pudo buscar en el catálogo');
    }
  };

  const linkToCanonical = async (canonical) => {
    if (!fixDialog?.pending) return;
    const primary_id = canonical.article_id;
    const absorbed_ids = [fixDialog.pending.article_id];
    const pendingName = fixDialog.pending.name;
    setFixDialog(null);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/article-merge/preview`,
        { primary_id, absorbed_ids }, { withCredentials: true });
      setMergeDialog({ primary_id, absorbed_ids, preview: r.data, source: 'fix-link', pendingName });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo calcular la vinculación');
    }
  };

  const applyRename = async () => {
    if (!fixDialog?.pending) return;
    const { pending, newName, newCategory, newUnit } = fixDialog;
    const name = (newName || '').trim();
    if (!name || name.length < 2) {
      toast.error('El nombre nuevo es demasiado corto.');
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/article-merge/pending-reviews/${pending.article_id}/rename`,
        { name, category: newCategory, unit: newUnit },
        { withCredentials: true }
      );
      toast.success(`Renombrado: "${r.data.old_name}" → "${r.data.new_name}"`);
      setFixDialog(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo renombrar');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-muted-foreground">Cargando…</div>;
  }

  return (
    <div className="space-y-6">
      {/* v3.41.1 — Guía embebida (reubicada desde Ayuda → /help/farmaceutico) */}
      <Card data-testid="homologation-guide-card" className="border-emerald-200 bg-emerald-50/40 dark:bg-emerald-950/10">
        <button
          type="button"
          onClick={toggleGuide}
          className="w-full text-left px-6 py-3 flex items-center gap-3 hover:bg-emerald-100/40 dark:hover:bg-emerald-900/20 transition-colors rounded-t-lg"
          data-testid="homologation-guide-toggle"
        >
          <BookOpen className="w-5 h-5 text-emerald-700 dark:text-emerald-300 shrink-0" />
          <div className="flex-1">
            <div className="font-semibold text-sm text-emerald-900 dark:text-emerald-100">
              Guía rápida: cómo homologar, fusionar y corregir
            </div>
            <div className="text-xs text-muted-foreground">
              Instructivo para farmacéuticos · incluye la nueva función "Corregir / Vincular"
            </div>
          </div>
          {guideOpen
            ? <ChevronUp className="w-4 h-4 text-emerald-700" />
            : <ChevronDown className="w-4 h-4 text-emerald-700" />}
        </button>
        {guideOpen && (
          <CardContent className="pt-0 pb-4" data-testid="homologation-guide-content">
            <div className="prose prose-sm dark:prose-invert max-w-none rounded-md border bg-background p-4 overflow-y-auto max-h-[500px]">
              {guideContent === null ? (
                <div className="text-xs text-muted-foreground">Cargando guía…</div>
              ) : (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{guideContent}</ReactMarkdown>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* ============== SECCIÓN A: HOMOLOGACIÓN DE ALTAS POR REM ============== */}
      <Card className={pending?.total > 0 ? 'border-amber-400' : ''} data-testid="pending-reviews-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-amber-600" />
            Homologación de altas por REM
            {pending?.total > 0 && <Badge className="bg-amber-500 text-white" data-testid="pending-count-badge">{pending.total}</Badge>}
            <Button variant="ghost" size="icon" onClick={load} className="ml-auto" data-testid="pending-refresh">
              <RefreshCw className="w-3 h-3" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            Cuando enfermería carga una REM y no encuentra el medicamento, el sistema crea la ficha automáticamente. Estas fichas quedan
            en <strong>modo pendiente</strong>: no aparecen en Compras (NP/OC) y, además, <strong>el stock de la REM queda congelado</strong> —
            no suma al depósito del área hasta que vos la homologues o la vincules a un canónico. Elegí una acción:
          </p>
          {pending?.total === 0 ? (
            <div className="text-emerald-700 flex items-center gap-2 text-sm">
              <ShieldCheck className="w-4 h-4" /> No hay altas pendientes de homologación.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Artículo</TableHead>
                  <TableHead>Origen</TableHead>
                  <TableHead>Stock congelado</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pending?.articles?.map((a) => (
                  <TableRow key={a.article_id} data-testid={`pending-row-${a.article_id}`}>
                    <TableCell>
                      <div className="font-semibold">{a.name}</div>
                      {a.review_context?.raw_name && a.review_context.raw_name !== a.name && (
                        <div className="text-xs text-muted-foreground">Original: "{a.review_context.raw_name}"</div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      <div>REM {a.review_context?.source_reception_number}</div>
                      <div className="text-muted-foreground">{a.review_context?.area_name} · {a.review_context?.created_by_name}</div>
                    </TableCell>
                    <TableCell className="text-xs">
                      {a.pending_stock?.total_qty > 0 ? (
                        <div className="space-y-0.5" data-testid={`pending-stock-${a.article_id}`}>
                          <div className="font-semibold text-amber-700">{a.pending_stock.total_qty} un.</div>
                          {a.pending_stock.warehouses?.map((w) => (
                            <div key={w.warehouse_id} className="text-muted-foreground">
                              → {w.name}: {w.qty} un.
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">{a.created_at?.slice(0, 10)}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button
                        size="sm" variant="outline"
                        onClick={() => setConfirmAction({
                          label: a.pending_stock?.total_qty > 0
                            ? `Homologar "${a.name}"? Pasará al catálogo maestro y se materializarán ${a.pending_stock.total_qty} unidades congeladas al stock del/los depósito(s).`
                            : `Homologar "${a.name}"? Pasará a ser un artículo válido del catálogo maestro.`,
                          onConfirm: () => homologate(a.article_id, a.name),
                        })}
                        disabled={busy}
                        data-testid={`homologate-btn-${a.article_id}`}
                      >
                        <ShieldCheck className="w-3 h-3 mr-1" /> Homologar
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => promptFuse(a, candidates?.groups, setCandidates, openMerge)}
                        disabled={busy}
                        data-testid={`fuse-btn-${a.article_id}`}
                      >
                        <Merge className="w-3 h-3 mr-1" /> Fusionar
                      </Button>
                      <Button
                        size="sm" variant="outline" className="text-blue-700 border-blue-300 hover:bg-blue-50"
                        onClick={() => openFixDialog(a)}
                        disabled={busy}
                        data-testid={`fix-btn-${a.article_id}`}
                        title="Corregir el nombre o vincular manualmente a un canónico existente"
                      >
                        <PenSquare className="w-3 h-3 mr-1" /> Corregir / Vincular
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ============== SECCIÓN B: FUSIÓN DE ARTÍCULOS DUPLICADOS ============== */}
      <Card data-testid="article-merge-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Merge className="w-4 h-4 text-blue-600" />
            Fusión de artículos duplicados
            {candidates?.groups?.length > 0 && <Badge className="bg-blue-500 text-white" data-testid="candidates-badge">{candidates.groups.length} grupos</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <Alert className="border-blue-300 bg-blue-50" data-testid="merge-info-banner">
            <ShieldCheck className="w-4 h-4 text-blue-700" />
            <AlertTitle className="text-blue-900">La fusión NO mueve stock entre depósitos</AlertTitle>
            <AlertDescription className="text-blue-900 text-xs leading-relaxed">
              Solo consolida el <strong>catálogo</strong>: un único artículo canónico bajo el nombre del principal.
              Cada lote conserva intacto su depósito, cantidad y trazabilidad.
              Cuando dos artículos duplicados tienen stock en el <em>mismo</em> subdepósito, las cantidades se suman ahí —
              el stock físico nunca cruza depósitos.
            </AlertDescription>
          </Alert>

          {/* v3.30.9 · Fusión masiva asistida por regla Manual/Compras */}
          {candidates?.groups?.length > 0 && (
            <div className="flex items-center justify-between gap-3 border border-purple-300 bg-purple-50 rounded p-3" data-testid="bulk-auto-panel">
              <div className="text-xs text-purple-900 leading-relaxed">
                <strong>Fusión masiva asistida:</strong> aplica automáticamente los grupos donde
                haya un artículo <strong>Manual/Compras</strong>, tomándolo como principal. Los grupos
                donde <em>todos</em> los miembros sean REM/externo quedan para revisión manual.
              </div>
              <Button
                size="sm"
                onClick={runBulkDryRun}
                disabled={busy}
                className="bg-purple-600 hover:bg-purple-700 shrink-0"
                data-testid="bulk-auto-dryrun-btn"
              >
                <Wand2 className="w-3 h-3 mr-1" /> Vista previa masiva
              </Button>
            </div>
          )}

          <p className="text-muted-foreground">
            Grupos detectados por nombre normalizado o similitud fuzzy. Elegí el artículo <strong>principal</strong> (canónico) y los duplicados se absorben.
            La ficha absorbida queda archivada apuntando al principal.
          </p>
          {!candidates?.groups?.length ? (
            <div className="text-emerald-700 flex items-center gap-2 text-sm">
              <ShieldCheck className="w-4 h-4" /> No se detectaron duplicados en {candidates?.total_articles || 0} artículos del catálogo.
            </div>
          ) : (
            <div className="space-y-3">
              {candidates.groups.map((g, gi) => (
                <MergeGroup key={gi} group={g} onOpen={openMerge} disabled={busy} testIdBase={`group-${gi}`} />
              ))}
            </div>
          )}
          {/* v3.45.2 · Fusión manual libre */}
          <div className="border border-dashed border-slate-300 rounded-lg p-4 space-y-4" data-testid="manual-merge-section">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <Link2 className="w-4 h-4" />
              Fusión manual — artículos no detectados automáticamente
            </div>
            <p className="text-xs text-muted-foreground">
              Buscá y elegí el artículo <strong>principal</strong> (el que queda) y el <strong>duplicado</strong> (el que se absorbe y archiva).
              El historial, stock y lotes del duplicado pasan al principal.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Principal */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-600">Principal (se conserva)</label>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 w-3.5 h-3.5 text-muted-foreground" />
                  <input
                    className="w-full pl-7 pr-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                    placeholder="Buscar artículo principal…"
                    value={manualQuery.primary}
                    data-testid="manual-primary-input"
                    onChange={e => { setManualPrimary(null); searchManual('primary', e.target.value); }}
                  />
                  {manualResults.primary.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full border rounded-md bg-background shadow-md max-h-48 overflow-y-auto">
                      {manualResults.primary.filter(a => a.article_id !== manualAbsorbed?.article_id).map(a => (
                        <button key={a.article_id} className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted flex items-center gap-2"
                          data-testid={`manual-primary-option-${a.article_id}`}
                          onClick={() => selectManual('primary', a)}>
                          <span className="flex-1">{a.name}</span>
                          {a.is_medicamento && <Badge variant="outline" className="text-[10px] border-rose-300 text-rose-600">Med.</Badge>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {manualPrimary && (
                  <div className="rounded bg-emerald-50 border border-emerald-200 px-3 py-1.5 text-xs text-emerald-800 flex items-center gap-2" data-testid="manual-primary-selected">
                    <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
                    <span className="font-medium">{manualPrimary.name}</span>
                    <span className="text-emerald-600 ml-auto">{manualPrimary.stock ?? '?'} u.</span>
                  </div>
                )}
              </div>

              {/* Duplicado a absorber */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-slate-600">Duplicado (se absorbe y archiva)</label>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 w-3.5 h-3.5 text-muted-foreground" />
                  <input
                    className="w-full pl-7 pr-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-ring"
                    placeholder="Buscar artículo duplicado…"
                    value={manualQuery.absorbed}
                    data-testid="manual-absorbed-input"
                    onChange={e => { setManualAbsorbed(null); searchManual('absorbed', e.target.value); }}
                  />
                  {manualResults.absorbed.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full border rounded-md bg-background shadow-md max-h-48 overflow-y-auto">
                      {manualResults.absorbed.filter(a => a.article_id !== manualPrimary?.article_id).map(a => (
                        <button key={a.article_id} className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted flex items-center gap-2"
                          data-testid={`manual-absorbed-option-${a.article_id}`}
                          onClick={() => selectManual('absorbed', a)}>
                          <span className="flex-1">{a.name}</span>
                          {a.is_medicamento && <Badge variant="outline" className="text-[10px] border-rose-300 text-rose-600">Med.</Badge>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {manualAbsorbed && (
                  <div className="rounded bg-red-50 border border-red-200 px-3 py-1.5 text-xs text-red-800 flex items-center gap-2" data-testid="manual-absorbed-selected">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    <span className="font-medium">{manualAbsorbed.name}</span>
                    <span className="text-red-600 ml-auto">{manualAbsorbed.stock ?? '?'} u.</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                size="sm"
                disabled={!manualPrimary || !manualAbsorbed || busy}
                onClick={openManualMerge}
                data-testid="manual-merge-preview-btn"
              >
                <Merge className="w-3.5 h-3.5 mr-1.5" />
                Ver vista previa de fusión
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      <Dialog open={!!mergeDialog} onOpenChange={(v) => !v && setMergeDialog(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="merge-preview-dialog"
                       onPointerDownOutside={(e) => e.preventDefault()}
                       onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Confirmar fusión</DialogTitle>
          </DialogHeader>
          {mergeDialog && (
            <div className="space-y-3 text-sm">
              <Alert>
                <Package className="w-4 h-4" />
                <AlertTitle>Principal (conservado)</AlertTitle>
                <AlertDescription>
                  <div>{mergeDialog.preview?.primary?.name}</div>
                  <div className="text-xs text-muted-foreground">Total actual: {mergeDialog.preview?.primary?.total_on_hand || 0} u</div>
                </AlertDescription>
              </Alert>
              <div>
                <div className="font-semibold mb-1">A absorber ({mergeDialog.preview?.absorbed?.length}):</div>
                <ul className="list-disc list-inside space-y-0.5">
                  {mergeDialog.preview?.absorbed?.map((a) => (
                    <li key={a.article_id}>{a.name} <span className="text-xs text-muted-foreground">({a.history_total || 0} registro/s · {a.total_on_hand || 0} u)</span></li>
                  ))}
                </ul>
              </div>

              {/* v3.30.1: Existencias antes/después por depósito */}
              <div className="border rounded p-2 bg-slate-50 space-y-2" data-testid="merge-stock-breakdown">
                <div className="font-semibold text-xs uppercase tracking-wide text-slate-700 flex items-center gap-1">
                  <Package className="w-3 h-3" /> Existencias por depósito — sin cambios físicos
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <div className="font-semibold mb-1 text-slate-600">Antes (dispersas por artículo)</div>
                    <ul className="space-y-1">
                      {[mergeDialog.preview.primary, ...(mergeDialog.preview.absorbed || [])].map((m, i) => (
                        (m?.stock_by_warehouse || []).length > 0 ? (
                          <li key={i}>
                            <div className="italic text-slate-500 truncate">{m.name}:</div>
                            <ul className="pl-3">
                              {m.stock_by_warehouse.map((row, ri) => (
                                <li key={ri}>· {row.warehouse_name}: <strong>{row.quantity} u</strong></li>
                              ))}
                            </ul>
                          </li>
                        ) : null
                      ))}
                      {[mergeDialog.preview.primary, ...(mergeDialog.preview.absorbed || [])].every(m => !(m?.stock_by_warehouse || []).length) && (
                        <li className="text-slate-500">Sin stock en ningún depósito.</li>
                      )}
                    </ul>
                  </div>
                  <div>
                    <div className="font-semibold mb-1 text-emerald-700">Después (consolidadas bajo el canónico)</div>
                    <ul className="space-y-0.5">
                      {(mergeDialog.preview.consolidated_stock_by_warehouse || []).map((row, ri) => (
                        <li key={ri}>· {row.warehouse_name}: <strong>{row.quantity} u</strong></li>
                      ))}
                      {!(mergeDialog.preview.consolidated_stock_by_warehouse || []).length && (
                        <li className="text-slate-500">—</li>
                      )}
                    </ul>
                    <div className="mt-2 text-[10px] text-slate-600 italic">
                      Mismo total, mismos depósitos. Solo cambia el nombre bajo el que se ven.
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="font-semibold mb-1">Total de registros a re-etiquetar: {mergeDialog.preview?.total_to_move || 0}</div>
                <div className="text-xs text-muted-foreground">Lotes, movimientos, órdenes de compra, pedidos internos, transferencias… se re-vinculan al artículo canónico. El historial se preserva íntegro.</div>
              </div>
              {mergeDialog.preview?.mirror_plan?.length > 0 && (
                <div>
                  <div className="font-semibold mb-1">Consolidación en subdepósitos:</div>
                  <ul className="list-disc list-inside space-y-0.5 text-xs">
                    {mergeDialog.preview.mirror_plan.map((m, i) => (
                      <li key={i}>
                        {m.action === 'merge'
                          ? `Sumar ${m.absorbed_stock}u + ${m.primary_mirror_stock}u en el mismo subdepósito`
                          : `Renombrar ${m.absorbed_stock}u al canónico (mismo subdepósito)`}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {mergeDialog.preview?.fields_to_fill && Object.keys(mergeDialog.preview.fields_to_fill).length > 0 && (
                <div className="text-xs text-muted-foreground">
                  Campos del principal a completar desde el absorbido: {Object.keys(mergeDialog.preview.fields_to_fill).join(', ')}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeDialog(null)} disabled={busy} data-testid="merge-cancel">Cancelar</Button>
            <Button onClick={confirmMerge} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="merge-confirm">
              {busy ? 'Aplicando…' : 'Confirmar fusión'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {confirmAction && (
        <ConfirmDialog
          open={!!confirmAction}
          onOpenChange={(v) => !v && setConfirmAction(null)}
          title={confirmAction.label}
          description=""
          confirmText="Aceptar"
          destructive={confirmAction.destructive !== false}
          loading={busy}
          onConfirm={async () => { const fn = confirmAction.onConfirm; setConfirmAction(null); await fn(); }}
          testId="article-merge-confirm"
        />
      )}

      {/* v3.41.0 — Modal "Corregir / Vincular" para fichas pendientes */}
      <Dialog open={!!fixDialog} onOpenChange={(v) => !v && setFixDialog(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="fix-dialog"
                       onPointerDownOutside={(e) => e.preventDefault()}
                       onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenSquare className="w-5 h-5 text-blue-600" />
              Corregir / Vincular ficha pendiente
            </DialogTitle>
          </DialogHeader>
          {fixDialog && (
            <div className="space-y-4 text-sm">
              <div className="rounded-lg border bg-amber-50/50 dark:bg-amber-950/20 border-amber-300 p-3">
                <div className="text-xs uppercase font-semibold text-amber-700 dark:text-amber-300">Ficha pendiente de REM</div>
                <div className="font-semibold text-base">{fixDialog.pending.name}</div>
                <div className="text-xs text-muted-foreground">
                  REM {fixDialog.pending.review_context?.source_reception_number} · {fixDialog.pending.review_context?.area_name} · {fixDialog.pending.created_at?.slice(0, 10)}
                </div>
              </div>

              {/* Tabs internos: Vincular vs Renombrar */}
              <div className="flex gap-1 border-b">
                <button
                  type="button"
                  onClick={() => setFixDialog((d) => d ? { ...d, mode: 'link' } : d)}
                  className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${fixDialog.mode === 'link' ? 'border-blue-600 text-blue-700' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                  data-testid="fix-tab-link"
                >
                  <Link2 className="w-3 h-3 inline mr-1" /> Vincular a canónico existente
                </button>
                <button
                  type="button"
                  onClick={() => setFixDialog((d) => d ? { ...d, mode: 'rename' } : d)}
                  className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${fixDialog.mode === 'rename' ? 'border-blue-600 text-blue-700' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                  data-testid="fix-tab-rename"
                >
                  <PenSquare className="w-3 h-3 inline mr-1" /> Renombrar la ficha pendiente
                </button>
              </div>

              {fixDialog.mode === 'link' && (
                <div className="space-y-3">
                  <p className="text-muted-foreground text-xs">
                    Buscá el artículo canónico correcto (ej. "QUETIAPINA 200MG"). Al vincular, se abre el preview de fusión: todos los lotes y movimientos de la ficha pendiente se re-etiquetan al canónico elegido y la ficha mal tipeada queda archivada.
                  </p>
                  <div className="flex gap-2 items-center">
                    <Search className="w-4 h-4 text-muted-foreground" />
                    <input
                      type="text"
                      autoFocus
                      className="flex-1 px-3 py-2 border rounded-md bg-background text-sm"
                      placeholder="Buscar en el catálogo canónico…"
                      value={fixDialog.query}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFixDialog((d) => d ? { ...d, query: v } : d);
                        clearTimeout(window.__fixSearchT);
                        window.__fixSearchT = setTimeout(() => searchCanonical(v), 300);
                      }}
                      data-testid="fix-search-input"
                    />
                  </div>
                  <div className="border rounded-md divide-y max-h-80 overflow-y-auto">
                    {fixDialog.searching && (
                      <div className="p-3 text-xs text-muted-foreground">Buscando…</div>
                    )}
                    {!fixDialog.searching && fixDialog.results.length === 0 && (
                      <div className="p-3 text-xs text-muted-foreground">
                        {fixDialog.query.length < 2
                          ? 'Escribí al menos 2 letras para buscar.'
                          : 'Sin coincidencias. Probá otro término o usá "Renombrar la ficha pendiente".'}
                      </div>
                    )}
                    {fixDialog.results.map((r) => (
                      <button
                        key={r.article_id}
                        type="button"
                        onClick={() => linkToCanonical(r)}
                        className="w-full px-3 py-2 text-left hover:bg-accent flex items-center gap-2 text-sm"
                        data-testid={`fix-result-${r.article_id}`}
                      >
                        <Package className="w-4 h-4 text-blue-600 shrink-0" />
                        <div className="flex-1">
                          <div className="font-medium">{r.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {r.category || 'Sin categoría'} · {r.unit || 'unidad'} · Stock: {Number(r.stock || 0)}
                            {r.is_medicamento && ' · 💊 medicamento'}
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs">{Math.round((r.score || 0) * 100)}%</Badge>
                        <Link2 className="w-4 h-4 text-blue-600" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {fixDialog.mode === 'rename' && (
                <div className="space-y-3">
                  <p className="text-muted-foreground text-xs">
                    Usá esta opción cuando NO existe un canónico y querés corregir el tipeo manteniendo la ficha. El cambio queda auditado (nombre viejo → nuevo). Después podrás Homologarla.
                  </p>
                  <div>
                    <label className="text-xs font-semibold">Nombre</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border rounded-md bg-background text-sm mt-1"
                      value={fixDialog.newName}
                      onChange={(e) => setFixDialog((d) => d ? { ...d, newName: e.target.value } : d)}
                      data-testid="fix-rename-name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold">Categoría</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border rounded-md bg-background text-sm mt-1"
                        value={fixDialog.newCategory}
                        onChange={(e) => setFixDialog((d) => d ? { ...d, newCategory: e.target.value } : d)}
                        placeholder="Opcional"
                        data-testid="fix-rename-category"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold">Unidad</label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border rounded-md bg-background text-sm mt-1"
                        value={fixDialog.newUnit}
                        onChange={(e) => setFixDialog((d) => d ? { ...d, newUnit: e.target.value } : d)}
                        placeholder="Opcional"
                        data-testid="fix-rename-unit"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFixDialog(null)} disabled={busy} data-testid="fix-cancel">
              Cancelar
            </Button>
            {fixDialog?.mode === 'rename' && (
              <Button onClick={applyRename} disabled={busy || !fixDialog?.newName?.trim()} data-testid="fix-rename-submit">
                {busy ? 'Renombrando…' : 'Renombrar ficha'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* v3.30.9 · Preview del plan masivo */}
      <Dialog open={!!bulkPlan} onOpenChange={(v) => !v && setBulkPlan(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="bulk-plan-dialog"
                       onPointerDownOutside={(e) => e.preventDefault()}
                       onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-purple-600" /> Vista previa · Fusión masiva asistida
            </DialogTitle>
          </DialogHeader>
          {bulkPlan && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-4 gap-2 text-center">
                <div className="border rounded p-2 bg-slate-50">
                  <div className="text-xs text-slate-500">Grupos detectados</div>
                  <div className="text-lg font-semibold">{bulkPlan.total_groups}</div>
                </div>
                <div className="border rounded p-2 bg-emerald-50">
                  <div className="text-xs text-emerald-700">A fusionar (regla OK)</div>
                  <div className="text-lg font-semibold text-emerald-800" data-testid="bulk-plan-ready">{bulkPlan.groups_ready_to_merge}</div>
                </div>
                <div className="border rounded p-2 bg-amber-50">
                  <div className="text-xs text-amber-700">Sin Manual/Compras</div>
                  <div className="text-lg font-semibold text-amber-800" data-testid="bulk-plan-skipped">{bulkPlan.groups_skipped}</div>
                </div>
                <div className="border rounded p-2 bg-blue-50">
                  <div className="text-xs text-blue-700">Registros a mover</div>
                  <div className="text-lg font-semibold text-blue-800">{bulkPlan.records_to_move_total || 0}</div>
                </div>
              </div>

              {bulkPlan.plan?.length > 0 && (
                <div>
                  <div className="font-semibold mb-1">Fusiones a aplicar</div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Principal (Manual/Compras)</TableHead>
                        <TableHead>Absorbe</TableHead>
                        <TableHead className="text-right">Registros</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bulkPlan.plan.map((p, i) => (
                        <TableRow key={i} data-testid={`bulk-plan-row-${i}`}>
                          <TableCell className="font-medium">{p.primary_name}</TableCell>
                          <TableCell className="text-xs">
                            <ul className="list-disc list-inside">
                              {p.absorbed.map((a) => (
                                <li key={a.article_id}>
                                  {a.name}
                                  {a.external_origin && <Badge className="ml-1 bg-slate-500 text-white text-[10px]">REM</Badge>}
                                  <span className="text-muted-foreground"> · {a.history_total} reg. · {a.total_on_hand}u</span>
                                </li>
                              ))}
                            </ul>
                          </TableCell>
                          <TableCell className="text-right">{p.records_to_move}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {bulkPlan.skipped?.length > 0 && (
                <div>
                  <div className="font-semibold mb-1 text-amber-800">Salteados para revisión manual</div>
                  <ul className="list-disc list-inside text-xs text-amber-900 space-y-0.5">
                    {bulkPlan.skipped.map((s, i) => (
                      <li key={i}>{s.reason}: {s.members?.join(' · ')}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkPlan(null)} disabled={busy} data-testid="bulk-plan-cancel">Cancelar</Button>
            <Button
              onClick={applyBulk}
              disabled={busy || !bulkPlan?.groups_ready_to_merge}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="bulk-plan-apply"
            >
              {busy ? 'Aplicando…' : `Aplicar ${bulkPlan?.groups_ready_to_merge || 0} fusión(es)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v3.30.9 · Resultado de la aplicación masiva */}
      <Dialog open={!!bulkApplyResult} onOpenChange={(v) => !v && setBulkApplyResult(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="bulk-result-dialog">
          <DialogHeader>
            <DialogTitle>Resultado · Fusión masiva</DialogTitle>
          </DialogHeader>
          {bulkApplyResult && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="border rounded p-2 bg-emerald-50">
                  <div className="text-xs text-emerald-700">Grupos fusionados</div>
                  <div className="text-lg font-semibold text-emerald-800">{bulkApplyResult.groups_merged}</div>
                </div>
                <div className="border rounded p-2 bg-blue-50">
                  <div className="text-xs text-blue-700">Registros movidos</div>
                  <div className="text-lg font-semibold text-blue-800">{bulkApplyResult.records_moved_total}</div>
                </div>
                <div className={`border rounded p-2 ${bulkApplyResult.errors?.length ? 'bg-red-50' : 'bg-slate-50'}`}>
                  <div className={`text-xs ${bulkApplyResult.errors?.length ? 'text-red-700' : 'text-slate-500'}`}>Errores</div>
                  <div className={`text-lg font-semibold ${bulkApplyResult.errors?.length ? 'text-red-800' : 'text-slate-700'}`}>{bulkApplyResult.errors?.length || 0}</div>
                </div>
              </div>
              {bulkApplyResult.errors?.length > 0 && (
                <div>
                  <div className="font-semibold mb-1 text-red-800">Errores</div>
                  <ul className="list-disc list-inside text-xs text-red-900 space-y-0.5">
                    {bulkApplyResult.errors.map((e, i) => (
                      <li key={i}><strong>{e.primary_name}:</strong> {e.detail}</li>
                    ))}
                  </ul>
                </div>
              )}
              {bulkApplyResult.skipped?.length > 0 && (
                <div>
                  <div className="font-semibold mb-1 text-amber-800">Quedaron para revisión manual</div>
                  <ul className="list-disc list-inside text-xs text-amber-900 space-y-0.5">
                    {bulkApplyResult.skipped.map((s, i) => (
                      <li key={i}>{s.reason}: {s.members?.join(' · ')}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setBulkApplyResult(null)} data-testid="bulk-result-close">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Bloque individual de "grupo de duplicados" con selector de principal. */
function MergeGroup({ group, onOpen, disabled, testIdBase }) {
  const [primaryId, setPrimaryId] = useState(group.members[0]?.article_id);
  const absorbed = group.members.filter((m) => m.article_id !== primaryId).map((m) => m.article_id);
  return (
    <div className="border rounded p-3 space-y-2" data-testid={testIdBase}>
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <span className="text-xs uppercase tracking-wide text-muted-foreground">{group.reason}</span>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">Principal</TableHead>
            <TableHead>Nombre</TableHead>
            <TableHead>Origen</TableHead>
            <TableHead>Existencias por depósito</TableHead>
            <TableHead className="text-right">Historial</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {group.members.map((m) => (
            <TableRow key={m.article_id} data-testid={`${testIdBase}-row-${m.article_id}`}>
              <TableCell>
                <input
                  type="radio"
                  name={`primary-${testIdBase}`}
                  checked={primaryId === m.article_id}
                  onChange={() => setPrimaryId(m.article_id)}
                  data-testid={`${testIdBase}-radio-${m.article_id}`}
                />
              </TableCell>
              <TableCell>
                <div>{m.name}</div>
                <div className="text-xs text-muted-foreground">
                  {m.unit || '—'}
                  {m.pending_catalog_review && <Badge className="ml-2 bg-amber-500 text-white text-[10px]">PENDIENTE</Badge>}
                  {m.external_origin && <Badge className="ml-2 bg-slate-500 text-white text-[10px]">EXTERNO</Badge>}
                </div>
              </TableCell>
              <TableCell className="text-xs">{m.external_origin ? 'REM/externo' : 'Manual/Compras'}</TableCell>
              <TableCell className="text-xs" data-testid={`${testIdBase}-stock-${m.article_id}`}>
                {(m.stock_by_warehouse || []).length === 0 ? (
                  <span className="text-slate-400">sin stock</span>
                ) : (
                  <div className="space-y-0.5">
                    <div className="font-semibold">{m.total_on_hand || 0} u total</div>
                    <ul className="text-[10px] leading-tight text-slate-600">
                      {m.stock_by_warehouse.map((row, ri) => (
                        <li key={ri}>· {row.warehouse_name}: <strong>{row.quantity}u</strong></li>
                      ))}
                    </ul>
                  </div>
                )}
              </TableCell>
              <TableCell className="text-right text-xs">{m.history_total || 0} reg.</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="text-right">
        <Button
          size="sm"
          disabled={disabled || absorbed.length === 0}
          onClick={() => onOpen(primaryId, absorbed)}
          data-testid={`${testIdBase}-merge-btn`}
        >
          <Merge className="w-3 h-3 mr-1" /> Fusionar ({absorbed.length})
        </Button>
      </div>
    </div>
  );
}

/** Helper: al presionar "Fusionar" en un pending, encontrar el grupo que contiene
 *  al pending y sugerir el matching (primer non-pending) como principal. Simplificación:
 *  el operador termina resolviendo en el dialogo de preview.
 */
function promptFuse(pending, groups, _setCandidates, openMerge) {
  const grupo = (groups || []).find((g) => g.members.some((m) => m.article_id === pending.article_id));
  if (!grupo) {
    toast.info('Este artículo no tiene duplicados candidatos aún. Usá "Homologar" o "Corregir / Vincular".');
    return;
  }
  const primary = grupo.members.find((m) => m.article_id !== pending.article_id && !m.pending_catalog_review) || grupo.members.find((m) => m.article_id !== pending.article_id);
  if (!primary) {
    toast.info('No hay un candidato principal para fusionar.');
    return;
  }
  openMerge(primary.article_id, [pending.article_id]);
}
